import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { HierarchyAggregationLevel, HierarchyModel, ProfileDTO, LoginDTO, CustomHierarchyPageLoadModel } from './add-custom-hierarchy.models';
import { AddCustomHierarchyService } from './add-custom-hierarchy.service';
import { Subject, Subscription } from "rxjs";
import { CdkScrollable, CdkVirtualScrollViewport, ScrollDispatcher } from '@angular/cdk/scrolling';
import { filter } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'cv-add-custom-hierarchy',
    templateUrl: './add-custom-hierarchy.component.html',
    styleUrls: ['./add-custom-hierarchy.component.scss']
})
export class AddCustomHierarchyComponent implements OnInit {

    /** Handles opening the modal for edits */
    @Input() openModalSubject: Subject<Object>;
    /** DOM reference to the content of the modal */
    @ViewChild('modalContent') modalContentRef: TemplateRef<any>;
    /**Used to send events when a hierarchy is saved */
    @Output() onModalSaved: EventEmitter<HierarchyModel> = new EventEmitter<HierarchyModel>();

    /** Internal reference to the modal for Nbg api calls */
    private modalRef: NgbModalRef = null;
    /** Options to use when the modal is popped open */
    readonly ngbModalOptions: NgbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        size: 'dialog-centered modal-lg'
    };

    //Private variables for controller only
    /** Collection of promises to wait for when the page loads */
    private _promiseCollection = [];
    /** Flag for if the modal is for editing (true) or adding a new Custom Hierarchy (false) */
    private isEditing: boolean = false;
    /** List of Hierarchy Models available. This is initially pulled from the PageLoad API call, but is manually updated when the modal is saved */
    private modelList: Array<HierarchyModel>;
    /** Internal list of all possible Roles for the dropdown in the modal */
    private allAvailableRoles: Array<ProfileDTO>;
    /** Internal list of all possible Logins for the dropdown in the modal */
    private allAvailableLogins: Array<LoginDTO>;
    /** The localization section to pull from */
    private readonly localizationSection: string = 'Pipeline_Configuration_Hierarchies';
    /** State field to determine if the modal or content is loading */
    private loading: boolean = true;

    //Public properties for front-end access
    /** Read-only wrapper to get if the modal is in edit mode (true) or add mode (false) */
    public get IsEditing(): boolean {
        return this.isEditing;
    }
    /** Read-only wrapper to get the text to place in the Roles Dropdown text placeholder */
    public get selectedRolesText() {
        if (this.model?.SelectedRoles == null || this.model.SelectedRoles.length < 1)
            return this.getLocalizedText('Roles_Placeholder', 'Select Role(s)');

        if (this.model.SelectedRoles.length == 1)
            return this.getLocalizedText('One_Role_Selected', '1 role selected');

        const msg = this.getLocalizedText('X_Roles_Selected', '{0} roles selected');
        return this.localizationService.Format(msg, this.model.SelectedRoles.length);
    }
    /** Read-only wrapper to get the text to place in the Logins Dropdown text placeholder */
    public get selectedLoginsText() {
        if (this.model?.SelectedLogins == null || this.model?.SelectedLogins.length < 1)
            return this.getLocalizedText('Users_Placeholder', 'Select User(s)');

        if (this.model.SelectedRoles.length == 1)
            return this.getLocalizedText('One_User_Selected', '1 user selected');

        const msg = this.getLocalizedText('X_Users_Selected', '{0} users selected');
        return this.localizationService.Format(msg, this.model.SelectedLogins.length);
    }
    /** Read-only wrapper to get if the modal or the content within is still loading */
    public get IsLoading(): boolean {
        return this.loading;
    }
    /** Read-only wrapper to get the localization section to pull from */
    public get LocalizationSection(): string {
        return this.localizationSection;
    }

    //Public fields for front-end access
    /** The model: (type HierarchyModel) to bind values to/from */
    public model: HierarchyModel = new HierarchyModel();
    /** Text field for searching and filtering within the Roles dropdown */
    public roleSearchTerm: string = "";
    /** Text field for searching and filtering within the Users dropdown */
    public userSearchTerm: string = "";
    /** The Roles to populate options with in the Roles dropdown. This is basically (all Roles - Selected Roles) */
    public availableRoles: ProfileDTO[];
    /** The Logins to populate options with in the Logins dropdown. This is basically (all Logins - Selected Logins) */
    public availableLogins: LoginDTO[];
    /** 'All Roles' option selected or not */
    public allRolesSelected: boolean = false;
    /** 'All Logins' option selected or not */
    public allLoginsSelected: boolean = false;

    constructor(
        private service: AddCustomHierarchyService,
        private localizationService: CVLocalizationService,
        private ngbModal: NgbModal,
        private ngZone: NgZone,
        private changeDetector: ChangeDetectorRef,
        readonly scrollDispatcher: ScrollDispatcher,
    ) {

    }

    async ngOnInit(): Promise<void> {
        //Start the loading process. Set the flag, queue up loading localization and customHierarchies, then wait and get the response.
        this.loading = true;
        this._promiseCollection.push(this.service.loadCustomHierarchies());
        this._promiseCollection.push(this.localizationService.GetSection(this.localizationSection));
        let response = await Promise.all(this._promiseCollection);

        //At this point, localization and customHierarchies are loaded (hierarchies are index 0 since they were added first). Init some critical collections.        
        const pageLoadModel = response[0] as CustomHierarchyPageLoadModel;
        this.modelList = pageLoadModel?.HierarchyModels ?? new Array();

        //Copy of all possible logins and roles for future editing
        this.allAvailableRoles = pageLoadModel?.AvailableRoles ?? new Array();
        this.allAvailableLogins = pageLoadModel?.AvailableLogins ?? new Array();

        //Subscribe to the openModalSubject (only used for editing as the New Hierarchy button triggers different method)
        this.openModalSubject.subscribe(customHierarchyId => {
            //Since the event is originating from AngularJS code, run in an ngZone call
            this.ngZone.run(_ => {
                const modelToModify = this.modelList.find(m => m.CustomHierarchyID == customHierarchyId);
                this.openModal(this.modalContentRef, modelToModify);
            });
        });

        //Initialize the onModelSaved event emitter if needed
        if (this.onModalSaved == null) {
            this.onModalSaved = new EventEmitter();
        }

        //Finally, indicate that loading is done
        this.loading = false;
    }

    ngAfterViewInit() {
        this.setupScrollHandler();
    }

    /** 
     * Prepares the model. Does things like making sure the model isn't undefined, that the SelectedRoles/Users are new Arrays (if new), 
     * and filters out SelectedRoles/Users from the dropdowns (if edit)
     * */
    private _prepareModel(existingRecord = null) {
        if (!existingRecord) { //New
            this.model = new HierarchyModel();
            this.model.SelectedRoles = new Array();
            this.model.SelectedLogins = new Array();
            this.model.AggregationLevels = [new HierarchyAggregationLevel()];

            this.availableRoles = this.allAvailableRoles.slice();
            this.availableLogins = this.allAvailableLogins.slice();
        }
        else { //Edit
            this.model = existingRecord;

            //TODO: After implementing saving logins and roles, filter out ones that are already on the model here
            this.availableRoles = this.allAvailableRoles.slice().filter(a => !this.model.SelectedRoles.includes(a));
            this.availableLogins = this.allAvailableLogins.slice().filter(a => !this.model.SelectedLogins.includes(a));
        }
    }

    /** Gets the cached text in the this.localizationSection by key with an optional defaultValue */
    public getLocalizedText(key: string, defaultValue: string): string {
        return this.localizationService.getCachedText(this.localizationSection, key, defaultValue);
    }

    /** Opens the modal for modifying a custom hierarchy record (eg recordToModify), or to make a new one if null
     * @param {TemplateRef<any>} modalContent - Content of the modal
     * @param {HierarchyModel} recordToModify - The HierarchyModel record to modify. If null, a new one will be made and prepared for the modal
     */
    openModal(modalContent, recordToModify = null): void {
        //If a HierarchyModel record was passed in, set isEditing to true. Then prepare the model for binding and open the modal
        this.isEditing = recordToModify != null;
        this._prepareModel(recordToModify);
        this.modalRef = this.ngbModal.open(modalContent, this.ngbModalOptions);
    }

    /** Add a new aggregation level */
    addAggregationLevel(): void {
        let newLevel = new HierarchyAggregationLevel();
        this.model.AggregationLevels.push(newLevel);
    }

    /** Remove an aggregation level at the specified index */
    removeAggregationLevel(index: number): void {
        //Since the ngFor loop is reversed, it returns the index backwards (0 is the last item, 1 is the second to last, etc)
        const reversedIndex = this.model.AggregationLevels.length - index - 1; //If 3 levels, and second item is clicked 3 - 1 - 1 = 1
        if (reversedIndex >= this.model.AggregationLevels.length)
            return;
        this.model.AggregationLevels.splice(reversedIndex, 1);
    }

    /** Dismiss the modal */
    closeModal(): void {
        if (this.modalRef)
            this.modalRef.dismiss();
    };

    /**
     * Takes the current this.model object and either pushes it to the modelList, or finds it and updates it in the list, depending on what this.isEditing is set to
     */
    private updateModelList() {
        if (!this.isEditing) {
            this.modelList.push(this.model);
        }
        else {
            const updateIndex = this.modelList.findIndex(m => m.CustomHierarchyID == this.model.CustomHierarchyID);
            if (updateIndex > -1)
                this.modelList[updateIndex] = this.model;
        }
    }

    /** Attempts to save the CustomHierarchy model. It will run through validation first, and if passed will hit the endpoint */
    async saveModal() {
        const validationResult = this.validateModalForSave();
        if (validationResult.passed) {
            try {                
                let saveResponse = await this.service.saveCustomHierarchy(this.model);
                this.model = saveResponse; //To get CustomHierarchyID updated on the model
                this.updateModelList();
                this.closeModal();
                this.onModalSaved.emit(this.model);
            }
            catch (error) {
                console.log("Save failed. Error: " + error);
            }
        }
        else {
            const errorMsgsJoined = validationResult.errors.join('<br/>-').slice(0, -1);
            const errorMsg = this.getLocalizedText('Error_Header', 'Please fix the following errors:') +
                ` <br/> -${errorMsgsJoined}`;
            showErrorMessage(errorMsg);
        }
    }

    /** Validate the this.model (HierarchyModel) for saving.
     * @returns {ValidationResult} Result of the validation testing along with errors list
     */
    validateModalForSave(): ValidationResult {
        let result: ValidationResult = {
            passed: true,
            errors: new Array()
        }

        // Make sure that name is specified and has at least a single character. Max length should be handled by the input box
        if (this.model.Name == null || this.model.Name.length < 1)
            this.validationFailsWith(result, this.getLocalizedText('Error_Name_Empty', 'Name cannot be empty.'));

        // Make sure there is at least a single login or single role selected (even though right now it isn't being saved anywhere)
        if (
            (this.model.SelectedRoles == null || this.model.SelectedRoles.length < 1) &&
            (this.model.SelectedLogins == null || this.model.SelectedLogins.length < 1)
        )
            this.validationFailsWith(result, this.getLocalizedText('Error_Need_Role_User', 'At least one user or role must be selected.'));

        // Make sure there is at least a single aggregation level
        if (this.model.AggregationLevels == null || this.model.AggregationLevels.length < 1)
            this.validationFailsWith(result, this.getLocalizedText('Error_Need_Aggregation_Level', 'At least one aggregation level must be specified.'));

        // Go through each aggregation level and make sure they each have a name
        this.model.AggregationLevels.forEach((level, idx) => {
            if (level.Name == null || level.Name.length < 1) {
                const defaultMsg = 'Aggregation at level {0} needs a name';
                const localizedMsg = this.getLocalizedText('Error_Level_Name_Missing', defaultMsg);
                const formattedMsg = this.localizationService.Format(localizedMsg, (idx + 1));
                this.validationFailsWith(result, formattedMsg);
            }
        });

        return result;
    }

    /** Simple validation fail wrapper that adds an error and fails the validation
     * @param {ValidationResult} result - The validation result to modify
     * @param {string} message - The error message to associate with the failed validation
     */
    validationFailsWith(result: ValidationResult, message: string): void {
        result.passed = false;
        result.errors.push(message);
    }

    /** Sets the model's SelectedRoles to be a copy of the availableRoles list */
    onRolesAllClicked(): void {
        this.model.SelectedRoles = this.availableRoles.map(r => r);
    }

    /** Removes all roles from the model */
    onRolesNoneClicked(): void {
        this.model.SelectedRoles = [];
    }

    /** Sets the model's SelectedLogins to be a copy of the availableLogins list */
    onLoginsAllClicked(): void {
        this.model.SelectedLogins = this.availableLogins.map(l => l);
    }

    /** Removes all logins from the model */
    onLoginsNoneClicked(): void {
        this.model.SelectedLogins = [];
    }

    /** Remove the selected role */
    removeSelectedRole(role: ProfileDTO): void {
        // Since mat-select is weird and seems to not allow direct modification of the list
        // It uses, reassign the entire list by filtering out the item that is being removed.
        this.model.SelectedRoles = this.model.SelectedRoles.filter(r => r.ProfileID != role.ProfileID);
    }

    /** Remove the selected login */
    removeSelectedLogin(login: LoginDTO): void {
        // Since mat-select is weird and seems to not allow direct modification of the list
        // It uses, reassign the entire list by filtering out the item that is being removed.
        this.model.SelectedLogins = this.model.SelectedLogins.filter(l => l.LoginID != login.LoginID);
    }

    /** Compares two roles for equality by ProfileID */
    public compareRoles(roleA: ProfileDTO, roleB: ProfileDTO): boolean {
        return roleA.ProfileID == roleB.ProfileID;
    }

    /** Compares two logins for equality by LoginID */
    public compareLogins(loginA: LoginDTO, loginB: LoginDTO): boolean {
        return loginA.LoginID == loginB.LoginID;
    }

    private _scrollableSubscription: Subscription;
    @ViewChildren(MatSelect) selects: QueryList<MatSelect>;
    @ViewChildren(CdkVirtualScrollViewport) cdkVirtualScrollViewPorts: QueryList<CdkVirtualScrollViewport>;
    @ViewChildren(MatOption) options: QueryList<MatOption>;

    setupScrollHandler() {
        // subscribe when one of our scrollable containers scroll
        this._scrollableSubscription = this.scrollDispatcher
            .scrolled()
            .pipe(filter<CdkScrollable>(scrollable => this.cdkVirtualScrollViewPorts.find(viewport => viewport === scrollable) != null))
            .subscribe((scrollable) => {
                // when scrolling we need to update the options if they are selected since they are being re-rendered by the CdkVirtualScroll.
                const element = scrollable.getElementRef();
                const selectType = element.nativeElement.attributes.getNamedItem("select-type").value;
                const selectedOptions = selectType === 'logins' ? new Set(this.model.SelectedLogins) : new Set(this.model.SelectedRoles);
                let needsUpdate = false;

                for (const option of this.options) {
                    if (option._getHostElement().attributes.getNamedItem("select-type")?.value !== selectType)
                        continue;

                    const selected = selectedOptions.has(option.value);
                    if (selected && !option.selected) {
                        option.select();
                        needsUpdate = true;
                    } else if (!selected && option.selected) {
                        option.deselect();
                        needsUpdate = true;
                    }
                }

                if (needsUpdate) {
                    this.changeDetector.detectChanges();
                }
            });
    }
}

/** Simple validation result interface for this controller with a boolean for successful validation and a list of error messages */
interface ValidationResult {
    passed: boolean;
    errors: string[];
}
